import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../constants/common'
import { SIZE } from '../../../../constants/size'
import { useNotification } from '../../../../hooks'
import { isRepeatedClick } from '../../../../utils/events'
import Button from '../../../Button'
import Modal, { Box, Buttons, Content, Header } from '../../../Modal'
import { MoreActionsButton } from '../../../MoreActionsButton'
import Text from '../../../Text'
import { EmailVariablesReportModal } from '../../EmailVariablesReportModal'
import { EMAIL_CREATION_FORM_FIELD_NAME_TO } from '../EmailCreationForm.constants'
import { getEmailFormTemplateFileName } from '../EmailCreationForm.utils'

import {
  QA_NAME_MISSING_VARIABLES,
  QA_NAME_VIEW_REPORT_BUTTON,
  QA_NAME_YES_BUTTON,
} from './EmailMissingVariablesModal.constants'
import {
  filterRecipients,
  filterReportByRecipients,
  getMoreOptions,
  toMoreActionsFromOptions,
} from './EmailMissingVariablesModal.utils'

import * as styles from './EmailMissingVariablesModal.module.css'

function EmailMissingVariablesModal({
  docTemplate,
  report,
  reportRecipients,
  onClose,
  onDeleteRecipients,
  onDeleteTemplate,
  onEditTemplate,
}) {
  const { t } = useTranslation()
  const { setFieldValue, values } = useFormikContext()

  const [isReportOpen, setReportOpen] = useState(false)

  const { handleAddSuccessNotification } = useNotification()

  const moreOptions = getMoreOptions({ report, t, template: docTemplate })

  function handleClose(event) {
    if (!isRepeatedClick(event)) {
      onClose()
    }
  }

  function handleDeleteRecipients() {
    const recipients = values[EMAIL_CREATION_FORM_FIELD_NAME_TO]

    setFieldValue(
      EMAIL_CREATION_FORM_FIELD_NAME_TO,
      filterRecipients(recipients, report)
    )
    handleAddSuccessNotification('Notification.RecipientsDeletedKey')
    onDeleteRecipients(filterReportByRecipients(report, recipients))
    onClose()
  }

  function handleDeleteTemplate() {
    onDeleteTemplate(docTemplate)
    handleAddSuccessNotification('Notification.TemplateDeletedKey')
    onClose()
  }

  function handleEditTemplate() {
    onEditTemplate({ editedTemplate: docTemplate, onClose })
  }

  const moreActions = toMoreActionsFromOptions({
    actionClassName: styles.action,
    moreOptions,
    onDeleteRecipients: handleDeleteRecipients,
    onDeleteTemplate: handleDeleteTemplate,
    onEditTemplate: handleEditTemplate,
  })

  function handleClickViewReport() {
    setReportOpen(true)
  }

  function handleCloseReport() {
    setReportOpen(false)
  }

  return isReportOpen ? (
    <EmailVariablesReportModal
      recipients={reportRecipients}
      onClose={handleCloseReport}
    />
  ) : (
    <Modal qaName={QA_NAME_MISSING_VARIABLES} onClose={handleClose}>
      <Box size={SIZE.MEDIUM}>
        <Header
          text={t('Email.MissingVariableContentModalTitleKey')}
          onClose={handleClose}
        />
        <Content className={styles.content}>
          {docTemplate ? (
            <Text Element='div' className={styles.fileName}>
              {getEmailFormTemplateFileName(docTemplate)}
            </Text>
          ) : null}
          <Text>{t('Email.MissingVariableContentModalMessageKey')}</Text>
          <Button.Text
            qaName={QA_NAME_VIEW_REPORT_BUTTON}
            text={t('Email.ViewReportBtnKey')}
            onClick={handleClickViewReport}
          />
        </Content>
        <Buttons>
          <Button.Contained
            qaName={QA_NAME_YES_BUTTON}
            text={t('Modal.YesBtnKey')}
            onClick={handleClose}
          />
          {moreActions.length > 0 ? (
            <MoreActionsButton
              actions={moreActions}
              actionsClassName={styles.actions}
              text={t('Modal.MoreBtnKey')}
              tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.bottomStart}
            />
          ) : null}
        </Buttons>
      </Box>
    </Modal>
  )
}

export default EmailMissingVariablesModal
